import React from 'react'
import { FormProvider,SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { resetPasswordSchema } from 'schemas/resetPasswordSchema'
import { useAppDispatch } from 'store'
import styled from 'styled-components'

import logoColor from 'assets/logoColor.png'
import { Button } from 'components/atoms/Button'
import { InputF } from 'components/atoms/Form/InputF'
import { useYupValidationResolver } from 'hooks/useYupValidationResolver'
import { resetPassword } from 'services/authSlice'
import { PATHS } from 'utils/paths'
import { isNotNilOrEmpty } from 'utils/ramda'
import { successToast } from 'utils/toast'

type ResetPasswordFields = {
  email: string
}

const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const resolver = useYupValidationResolver(resetPasswordSchema)
  const methods = useForm<ResetPasswordFields>({
    resolver
  })

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    reset
  } = methods

  const isErrors = isNotNilOrEmpty(errors)

  const onSubmit: SubmitHandler<ResetPasswordFields> = async (
    data: ResetPasswordFields
  ) => {
    const action = await dispatch(resetPassword(data))

    if (action.meta.requestStatus === 'fulfilled') {
      successToast('Now please check your email')
      reset({ email: '' })
    }
  }

  const navigateToLogin = () => {
    return navigate(PATHS.login)
  }

  return (
    <Wrapper>
      <LogoWrapper onClick={navigateToLogin}>
        <Logo src={logoColor} alt='logo' />
      </LogoWrapper>
      <FormProvider {...methods}>
        <Title>RESET PASSWORD</Title>
        <Form onSubmit={handleSubmit(onSubmit)} sciserrors={isErrors}>
          <InputF name='email' label='Email' type='text' size='large' />
          <Login type='button' onClick={navigateToLogin}>
            Login
          </Login>
          <Button
            variant='primary'
            size='medium'
            type='submit'
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Reset Password
          </Button>
        </Form>
      </FormProvider>
      <Copyright>
        <span>&#169; SINDYCAT</span>
      </Copyright>      
      <Links>
        <PrivacyPolicy
          onClick={() =>
            window.open(
              `${PATHS.privacyPolicy}`,
              '_blank',
              'noopener,noreferrer'
            )
          }
        >
          Privacy Policy
        </PrivacyPolicy>
        |
        <MailTo href='mailto:office@digitalshelf.pl'>
          office@digitalshelf.pl
        </MailTo>
      </Links>
      <BottomLine />
    </Wrapper>
  )
}

export default ResetPasswordPage

const Wrapper = styled.div`
  position: relative;
  height: 100vh;
  width: 100%;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`
const Title = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 32px;
  line-height: 26px;
  margin-bottom: 20px;
`
const Form = styled.form<{ sciserrors: boolean }>`
  width: 516px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex-direction: column;
  gap: ${({ sciserrors }) => (sciserrors ? '45px' : '30px')};
`
const Login = styled.button`
  border: 0;
  padding: 0;
  margin-bottom: 10px;
  background-color: unset;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
`
const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 32px;
  left: 32px;
`
const Logo = styled.img`
  height: ${({ theme }) => theme.dimensions.logoColorHeight + 'px'};
`
const BottomLine = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 24px;
  background: ${({ theme }) => theme.gradients.bottomLine};
`
const Copyright = styled.div`
  position: absolute;
  bottom: 60px;
`
const Links = styled.div`
  position: absolute;
  bottom: 40px;
  right: 50px;
  display: flex;
  gap: 12px;
`
const PrivacyPolicy = styled.div`
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`
const MailTo = styled.a`
  color: blue;
`
